import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
} from '@mui/material'
import { useAuth } from '../../context/AuthContext'
import { useTenant } from '../../context/TenantContext'
import { setTenantSlug } from '../../lib/api'
import { resolveTenantSlug } from '../../lib/tenantUtils'
import { loginSchema, inviteEmailSchema } from '../../lib/schemas'
import { STANDARD_FORM_OPTIONS, isSubmitDisabled } from '../../lib/formDefaults'
import { getApiErrorMessage } from '../../lib/apiErrors.js'
import { Logo, PoweredByLogo } from '../common/Logo'
import { roleHomePath } from '../../lib/roleHomePath'

const MAGIC_LINK_SUCCESS_MESSAGE = 'If that account exists, we sent a sign-in link.'

/** Explicit login form colors for WCAG AA on white paper (avoids low-contrast CSS variable edge cases). */
const LOGIN_A11Y = {
  ink: '#2D3748',
  muted: '#4A5568',
  brand: '#6B5A9E',
  brandHover: '#5a4a85',
  disabledBg: '#A0AEC0',
}

const loginFieldSx = {
  mb: 2,
  '& .MuiInputLabel-root': { color: `${LOGIN_A11Y.muted} !important` },
  '& .MuiInputLabel-root.Mui-focused': { color: `${LOGIN_A11Y.muted} !important` },
  '& .MuiInputLabel-root.Mui-disabled': { color: `${LOGIN_A11Y.muted} !important` },
}

/**
 * LoginForm component - Handles user authentication
 */
export const LoginForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { signIn, fetchUserProfile, userProfile, loading: authLoading } = useAuth()
  const { tenant } = useTenant()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    ...STANDARD_FORM_OPTIONS,
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const email = watch('email')
  const magicLinkEmailValid = inviteEmailSchema.safeParse({ email: email || '' }).success
  const [showMagicLink, setShowMagicLink] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [magicLinkMessage, setMagicLinkMessage] = useState(MAGIC_LINK_SUCCESS_MESSAGE)
  const [magicLinkDevUrl, setMagicLinkDevUrl] = useState(null)
  const [error, setError] = useState('')
  const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || 'support@thesocialbox.co'
  const isInvalidCredentialsError = /unauthorized|invalid credentials|incorrect/i.test(String(error || '').toLowerCase())

  // Clear stale X-Tenant from view-as / tenant subdomain unless URL explicitly scopes login.
  useEffect(() => {
    const urlTenant = resolveTenantSlug(searchParams)
    if (!urlTenant) setTenantSlug(null)
  }, [searchParams])

  // Magic link: /login?magic=TOKEN
  useEffect(() => {
    const magicToken = searchParams.get('magic')
    if (!magicToken) return
    let cancelled = false
    ;(async () => {
      try {
        const api = (await import('../../lib/api')).default
        const data = await api.auth.magicLinkVerify(magicToken)
        await fetchUserProfile()
        if (!cancelled && data?.user?.role) {
          navigate(roleHomePath(data.user), { replace: true })
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Invalid or expired magic link.')
      }
    })()
    return () => { cancelled = true }
  }, [searchParams, fetchUserProfile, navigate])

  // Redirect if already logged in (handle /login and /login/ trailing slash)
  const isLoginPage = location.pathname === '/login' || location.pathname === '/login/'
  useEffect(() => {
    if (userProfile && !authLoading && isLoginPage) {
      if (userProfile.onboarding_complete === false && userProfile.role !== 'influencer') {
        navigate('/complete-onboarding', { replace: true })
        return
      }
      navigate(roleHomePath(userProfile), { replace: true })
    }
  }, [userProfile, navigate, authLoading, isLoginPage])

  const onSubmit = async (data) => {
    setError('')
    const { error: signInError, data: signInData } = await signIn(data.email, data.password)
    if (signInError) {
      const msg = getApiErrorMessage(signInError)
      const lower = msg.toLowerCase()
      const isInvalidLogin = signInError?.status === 401 || /incorrect|invalid credentials/i.test(lower)
      const isPendingReset =
        signInError?.status === 403 && /password reset|setup link/i.test(lower)
      setError(
        isPendingReset
          ? `${msg} Password sign-in is turned off until you open that link and set a new password. In local dev, check backend logs for [EMAIL STUB] or ask a super admin to send a new reset from People Directory.`
          : isInvalidLogin
            ? 'Email or password is incorrect. Please try again, or request a sign-in link.'
            : `${msg} If this continues, contact ${supportEmail}.`
      )
      return
    }
    // Redirect immediately after successful login (in case useEffect doesn't run, e.g. pathname quirk)
    if (signInData?.user) {
      navigate(roleHomePath(signInData.user), { replace: true })
    }
  }

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault()
    if (!email?.trim()) return
    setError('')
    setMagicLinkDevUrl(null)
    try {
      const api = (await import('../../lib/api')).default
      const data = await api.auth.magicLinkRequest(email)
      setMagicLinkSent(true)
      if (data?.message) setMagicLinkMessage(data.message)
      if (data?.devLink) setMagicLinkDevUrl(data.devLink)
    } catch (err) {
      setError(err.message || 'Failed to send link')
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: (theme) => theme.palette.background.default,
      }}
    >
      {/* Image Section - Hidden on mobile */}
      <Box
        component="aside"
        aria-label="Product overview"
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: { md: '50%' },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/login-bg.png"
          alt=""
          loading="eager"
          fetchPriority="high"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, rgba(0, 0, 0, 0.35) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
            <Logo variant="white" height={120} sx={{ mb: 2, mx: 'auto' }} />
            <Typography variant="h4" sx={{ opacity: 0.95, fontWeight: 600 }}>
              Run campaigns. Not spreadsheets.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Form Section */}
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: { xs: '100%', md: '50%' },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 3, sm: 4, md: 5 },
            width: '100%',
            borderRadius: 3,
            background: (theme) => theme.palette.background.paper,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            animation: 'fadeInUp 0.5s ease-out',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
            {tenant?.logoUrl ? (
              <Box component="img" src={tenant.logoUrl} alt="" sx={{ height: 96, mb: 1, objectFit: 'contain' }} />
            ) : (
              <Logo variant="periwinkle" height={96} sx={{ mb: 1 }} />
            )}
            {tenant?.displayName && (
              <Typography component="p" variant="h6" align="center" sx={{ mt: 0.5, color: LOGIN_A11Y.muted }}>
                {tenant.displayName}
              </Typography>
            )}
          </Box>
          <Typography
            component="h1"
            variant="h5"
            align="center"
            gutterBottom
            sx={{ mb: 4, fontWeight: 600, color: LOGIN_A11Y.ink }}
          >
            Sign In
          </Typography>

          {error && (
            <Alert severity={isInvalidCredentialsError ? 'warning' : 'error'} sx={{ mt: 2, mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {!!error && !isInvalidCredentialsError && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Include the time of failure and workspace URL when contacting support.
            </Typography>
          )}

          <Box sx={{ mt: 1 }}>
            {!showMagicLink ? (
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <TextField
                  margin="normal"
                  fullWidth
                  label="Email Address"
                  type="email"
                  required
                  autoComplete="email"
                  disabled={isSubmitting}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={loginFieldSx}
                  {...register('email')}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  label="Password"
                  type="password"
                  required
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  error={!!errors.password || isInvalidCredentialsError}
                  helperText={errors.password?.message || (isInvalidCredentialsError ? 'Double-check your password and try again.' : '')}
                  sx={loginFieldSx}
                  {...register('password')}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    bgcolor: `${LOGIN_A11Y.brandHover} !important`,
                    color: '#fff !important',
                    '&:hover': { bgcolor: '#5a4a85 !important' },
                    '&.Mui-disabled': {
                      bgcolor: `${LOGIN_A11Y.disabledBg} !important`,
                      color: '#fff !important',
                    },
                  }}
                  disabled={isSubmitDisabled({ isValid, isSubmitting })}
                >
                  {isSubmitting ? 'Signing In...' : 'Sign In'}
                </Button>
                <Box textAlign="center" sx={{ mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setShowMagicLink(true)}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: `${LOGIN_A11Y.brand} !important`,
                      '&:hover': {
                        textDecoration: 'underline',
                        color: `${LOGIN_A11Y.brandHover} !important`,
                      },
                    }}
                  >
                    Email me a sign-in link
                  </Link>
                </Box>
              </Box>
            ) : !magicLinkSent ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Enter your email and we&apos;ll send you a one-time sign-in link.
                </Typography>
                <Box component="form" onSubmit={handleMagicLinkSubmit} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Email"
                    type="email"
                    required
                    value={email || ''}
                    onChange={(e) => setValue('email', e.target.value, { shouldValidate: true })}
                    error={!magicLinkEmailValid && Boolean((email || '').trim())}
                    helperText={
                      !magicLinkEmailValid && (email || '').trim()
                        ? 'Please enter a valid email address'
                        : ''
                    }
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="medium"
                    disabled={isSubmitDisabled({
                      isValid: magicLinkEmailValid,
                      isSubmitting,
                    })}
                  >
                    Send link
                  </Button>
                </Box>
                <Link component="button" type="button" variant="body2" onClick={() => { setShowMagicLink(false); setMagicLinkSent(false); setMagicLinkMessage(MAGIC_LINK_SUCCESS_MESSAGE); setMagicLinkDevUrl(null) }} sx={{ mt: 1, display: 'block', cursor: 'pointer' }}>
                  Back to password
                </Link>
              </Box>
            ) : (
              <Box>
                <Alert severity="success">{magicLinkMessage}</Alert>
                {magicLinkDevUrl && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2" component="span">Dev mode: no email sent. Use this link: </Typography>
                    <Link href={magicLinkDevUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                      {magicLinkDevUrl}
                    </Link>
                  </Alert>
                )}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => {
                    setShowMagicLink(false)
                    setMagicLinkSent(false)
                    setMagicLinkMessage(MAGIC_LINK_SUCCESS_MESSAGE)
                    setMagicLinkDevUrl(null)
                  }}
                  sx={{ mt: 1.5, display: 'inline-block', cursor: 'pointer' }}
                >
                  Back to login
                </Link>
              </Box>
            )}
            {tenant && <PoweredByLogo />}
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}
